import React from 'react';
import { FileText } from 'lucide-react';
import type { UpdatePreferencesInput } from '../../types/preference.types';

interface BusinessReportingSettingsSectionProps {
  formData: UpdatePreferencesInput;
  onChange: (patch: UpdatePreferencesInput) => void;
}

export function BusinessReportingSettingsSection({
  formData,
  onChange,
}: BusinessReportingSettingsSectionProps): React.ReactElement {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Business &amp; tax reporting
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Shown on Business Expense PDF exports. GST summary sections appear only when GST registered.
          Set business-use % when generating a{' '}
          <span className="font-medium text-gray-800 dark:text-gray-200">Business Expense</span> report
          (step 2 — Business claims).
        </p>
      </div>
      <div className="space-y-4">
        <div>
          <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Business name
          </label>
          <input
            id="businessName"
            type="text"
            value={formData.businessName ?? ''}
            onChange={(e) => onChange({ businessName: e.target.value || null })}
            className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Trading name"
          />
        </div>
        <div>
          <label htmlFor="businessAbn" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            ABN
          </label>
          <input
            id="businessAbn"
            type="text"
            inputMode="numeric"
            value={formData.businessAbn ?? ''}
            onChange={(e) => onChange({ businessAbn: e.target.value.replace(/\s/g, '') || null })}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="11 digits"
          />
        </div>
        <div className="flex items-center justify-between py-2 max-w-md">
          <div>
            <label htmlFor="gstRegistered" className="font-medium text-gray-900 dark:text-gray-100">
              GST registered
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Enables GST blocks on business expense reports (informational).
            </p>
          </div>
          <button
            type="button"
            id="gstRegistered"
            onClick={() => onChange({ gstRegistered: !formData.gstRegistered })}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              formData.gstRegistered ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
            role="switch"
            aria-checked={Boolean(formData.gstRegistered)}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.gstRegistered ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
